import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@nvision/identity-core';
import { Button, Spinner } from '@nvision/ui';

import { InfiniteScroll } from '@/components/infinite-scroll';
import { Page } from '@/components/page';

import { getDefaultAccessorialsFormValues, useAccessorials, useAccessorialsFilters } from '../../hooks';
import { AccessorialsSearchFiltersForm, type AccessorialsSearchFiltersFormRef } from '../forms';
import { AccessorialCard } from './accessorial-card';
import { AccessorialsControls } from './accessorials-controls';

import styles from './accessorials.module.css';

const SCROLL_OFFSET = 200;

export const Accessorials: React.FC = () => {
  const { clearFilters, customerKey, filters, onFiltersChange } = useAccessorialsFilters();
  const { t } = useTranslation();
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const formRef = useRef<AccessorialsSearchFiltersFormRef>({} as AccessorialsSearchFiltersFormRef);
  const prevCustomerKeyRef = useRef<number | null>(null);

  useEffect(() => {
    if (prevCustomerKeyRef.current !== null && prevCustomerKeyRef.current !== customerKey && customerKey) {
      formRef.current?.reset(getDefaultAccessorialsFormValues(timeZone));
      clearFilters();
    }

    if (customerKey) {
      prevCustomerKeyRef.current = customerKey;
    }
  }, [customerKey, clearFilters, timeZone]);

  const {
    data: accessorials,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
  } = useAccessorials(filters, customerKey);

  return (
    <Page>
      <Page.SideBar>
        <Page.SideBar.Header title="Accessorial Search" />
        <Page.SideBar.Main>
          {!filters ? (
            <div className={styles.loading}>
              <Spinner size="lg" />
            </div>
          ) : (
            <AccessorialsSearchFiltersForm
              customerKey={customerKey}
              initialValues={filters}
              formRef={formRef}
              onSubmit={onFiltersChange}
            />
          )}
        </Page.SideBar.Main>
        <Page.SideBar.Footer>
          <Button
            secondary
            size="sm"
            onClick={() => formRef.current?.reset(getDefaultAccessorialsFormValues(timeZone))}
          >
            {t('translation:default.buttons.clear')}
          </Button>

          <Button type="submit" size="sm" primary onPress={() => formRef.current?.submit()}>
            {t('translation:default.buttons.search')}
          </Button>
        </Page.SideBar.Footer>
      </Page.SideBar>
      <Page.Main>
        <AccessorialsControls />
        {!filters || isLoading || (isRefetching && !isFetchingNextPage) ? (
          <div className={styles.loading}>
            <Spinner size="lg" />
          </div>
        ) : (
          <InfiniteScroll
            className={styles.list}
            hasMore={hasNextPage}
            isLoading={isFetchingNextPage}
            loadMore={fetchNextPage}
            scrollOffset={SCROLL_OFFSET}
          >
            {accessorials?.items.map((accessorial) => (
              <AccessorialCard key={accessorial.accessorialKey} accessorial={accessorial} />
            ))}
          </InfiniteScroll>
        )}
      </Page.Main>
    </Page>
  );
};
